import { applyChartFacts, mergePatientDemographics } from '../studies/applyChartFacts';

function asText(value) {
  if (Array.isArray(value)) return value.map((item) => asText(item)).filter(Boolean).join(' ');
  if (value == null || value === false) return '';
  return String(value).trim();
}

function isoDate(value) {
  const match = asText(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

function isoTime(value) {
  const match = asText(value).match(/T(\d{2}:\d{2})/);
  return match ? match[1] : '';
}

function procedureText(recommendation, chartFacts) {
  const parts = [];
  const inputs = recommendation?.inputs;
  if (Array.isArray(inputs)) {
    inputs.forEach((item) => {
      const variable = asText(item?.variable).toLowerCase();
      if (variable === 'level of intervention' || variable === 'type of intervention' || variable === 'diagnosis') {
        const value = asText(item?.value);
        if (value) parts.push(value);
      }
    });
  }
  if (recommendation?.reason_coding_code) parts.push(asText(recommendation.reason_coding_code));
  const surgery = recommendation?.parsed_output?.surgery;
  if (Array.isArray(surgery)) {
    surgery.forEach((item) => {
      if (item?.procedure) parts.push(asText(item.procedure));
    });
  }
  if (chartFacts?.procedure_text) parts.push(asText(chartFacts.procedure_text));
  return parts.join(' ');
}

function inferCase(chartFacts, procedures) {
  const diagnoses = chartFacts?.diagnoses || [];
  const blob = `${diagnoses.map((row) => row.description || '').join(' ')} ${procedures}`.toLowerCase();
  const tags = new Set(diagnoses.flatMap((row) => row.tags || []));
  const deformity = tags.has('scoliosis') || tags.has('degenerative_scoliosis') || tags.has('lordosis')
    || /scoliosis|kyphosis|adult spinal deformity|\basd\b/.test(blob);
  const cervical = diagnoses.some((row) => row.body_part === 'cervical') || /cerv|\bc\d/.test(blob);
  const thoracolumbar = diagnoses.some((row) => row.body_part === 'thoracolumbar') || /lumb|thorac|\b[tls]\d/.test(blob);
  if (deformity) {
    return {
      arm: 'Adult Spinal Deformity',
      region: cervical && !thoracolumbar ? 'Cervical' : 'Thoracolumbar',
    };
  }
  if (thoracolumbar && !cervical) return { arm: 'Thoracolumbar', region: '' };
  return { arm: 'Cervical', region: '' };
}

export function prefillAllianceChart({ patient = {}, chartFacts = null, recommendation = null } = {}) {
  const facts = mergePatientDemographics(chartFacts, patient);
  const procedures = procedureText(recommendation, facts);
  const { arm, region } = inferCase(facts, procedures);
  const applied = applyChartFacts(facts, {
    arm,
    region,
    procedureText: procedures,
    indexDate: isoDate(facts?.index_date || patient?.surgery_start_time),
    indexTime: isoTime(patient?.surgery_start_time),
  });
  return {
    active: { phaseId: 'preop', visitId: '', formId: 'indication' },
    forms: applied.alliance.forms,
  };
}
